import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useLocation } from "wouter";
import { Textarea } from "../ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { useState, useEffect } from "react";
import { createBooking } from "../../api/api";
import { formatCurrency } from "../../lib/utils";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import AuthModal from "../auth/AuthModal";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";

const formSchema = z.object({
    notes: z.string().max(500, "Notes must be 500 characters or less").optional(),
});
type NotesFormValues = z.infer<typeof formSchema>;

const ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="mr-2 mt-1 h-4 w-4 text-green-500"
        viewBox="0 0 20 20"
        fill="currentColor"
    >
        <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
        />
    </svg>
);

const BookingShuffle = () => {
    const [, setLocation] = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const { isAuthenticated } = useAuth();
    const { toast } = useToast();

    // Load shuffleResult and bookingContext
    const shuffleResult = JSON.parse(localStorage.getItem("shuffleResult") || "[]");
    const bookingContext = JSON.parse(localStorage.getItem("shuffleBookingContext") || "{}");

    // Find the highest price among the shuffled restaurants
    const highestPrice = shuffleResult.length
        ? Math.max(...shuffleResult.map((rest: any) => rest.price))
        : parseFloat(bookingContext.pricePerPerson);

    const {
        location,
        date,
        time,
        guests,
        dietaryRestrictions,
    } = bookingContext;
    const pricePerPerson = highestPrice;
    const guestsNumber = parseInt(guests, 10) || 1;
    const totalPrice = pricePerPerson * guestsNumber;

    useEffect(() => {
        if (bookingContext && bookingContext.pricePerPerson !== pricePerPerson) {
            localStorage.setItem(
                "shuffleBookingContext",
                JSON.stringify({
                    ...bookingContext,
                    pricePerPerson,
                })
            );
        }
    }, []);

    const form = useForm<NotesFormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: { notes: "" },
    });

    const onSubmit = async (data: NotesFormValues) => {
        if (!isAuthenticated) {
            setIsAuthModalOpen(true);
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await createBooking({
                ...bookingContext,
                pricePerPerson,
                total_price: totalPrice,
                notes: data.notes || "",
                payment_session_id: bookingContext.payment_session_id,
                payment_token: bookingContext.payment_token,
            });
            localStorage.removeItem("shuffleResult");
            localStorage.removeItem("shuffleBookingContext");
            toast({
                title: "Good!",
                description: `You will be redirected to payment page ❤️`,
                variant: "default",
            });

            if (response?.payment_url) {
                window.location.href = response.payment_url;
            } else {
                toast({
                    title: "Opps!",
                    description: `Something went wrong. Please try again!`,
                    variant: "destructive",
                });
            }
        } catch (error: any) {
            alert("Booking failed. Please try again.");
            setLocation("/failed");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="bg-gray-50 py-6 transition-colors duration-200 dark:bg-gray-900 min-h-screen">
            <div className="container mx-auto px-2 sm:px-4 lg:px-6">
                <div className="overflow-hidden rounded-xl bg-white shadow-lg dark:bg-gray-800">
                    <div className="md:flex h-full min-h-[380px]">
                        {/* --- LEFT IMAGE, smaller height --- */}
                        <div className="relative md:w-1/2 w-full h-44 md:h-auto min-h-[250px] md:min-h-[380px]">
                            <img
                                src="https://images.pexels.com/photos/6267/menu-restaurant-vintage-table.jpg"
                                alt="SurpriseTable Restaurant"
                                className="object-cover w-full h-full"
                                style={{ minHeight: 250, height: "100%" }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        </div>

                        {/* --- RIGHT CONTENT --- */}
                        <div className="p-4 md:w-1/2 md:p-6 flex flex-col justify-between">
                            {/* STEP TITLE */}
                            <div className="mb-2">
                                <h2 className="font-serif text-xl font-bold text-gray-800 dark:text-white mb-1">
                                    Last step!
                                </h2>
                                <p className="text-gray-700 dark:text-gray-300 text-sm mt-1">
                                    Please review your booking details and confirm below.
                                </p>
                            </div>

                            {/* BOOKING DETAILS */}
                            <div className="mb-3">
                                <dl className="grid grid-cols-1 gap-y-1 sm:grid-cols-2 sm:gap-x-3">
                                    <div>
                                        <dt className="font-semibold text-gray-600 dark:text-gray-400 text-xs">City</dt>
                                        <dd className="text-gray-800 dark:text-gray-100 text-sm">{location}</dd>
                                    </div>
                                    <div>
                                        <dt className="font-semibold text-gray-600 dark:text-gray-400 text-xs">Date</dt>
                                        <dd className="text-gray-800 dark:text-gray-100 text-sm">{date}</dd>
                                    </div>
                                    <div>
                                        <dt className="font-semibold text-gray-600 dark:text-gray-400 text-xs">Time</dt>
                                        <dd className="text-gray-800 dark:text-gray-100 text-sm">{time}</dd>
                                    </div>
                                    <div>
                                        <dt className="font-semibold text-gray-600 dark:text-gray-400 text-xs">Guests</dt>
                                        <dd className="text-gray-800 dark:text-gray-100 text-sm">{guests}</dd>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <dt className="font-semibold text-gray-600 dark:text-gray-400 text-xs">Dietary restrictions</dt>
                                        <dd className="text-gray-800 dark:text-gray-100 text-sm">
                                            {Array.isArray(dietaryRestrictions) && dietaryRestrictions.length > 0
                                                ? dietaryRestrictions.join(", ")
                                                : <span className="text-gray-500">None</span>
                                            }
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            {/* ABOUT YOUR SURPRISE */}
                            <div className="mb-3 border-t border-gray-200 py-2 dark:border-gray-700">
                                <h3 className="mb-2 text-base font-semibold text-gray-800 dark:text-white">
                                    What to Expect
                                </h3>
                                <ul className="space-y-2 text-sm">
                                    <li className="flex items-start">{ICON}
                                        <span className="text-gray-600 dark:text-gray-300">
                                            Chef-selected menu valued higher than you pay
                                        </span>
                                    </li>
                                    <li className="flex items-start">{ICON}
                                        <span className="text-gray-600 dark:text-gray-300">
                                            Fully accommodates your dietary preferences
                                        </span>
                                    </li>
                                    <li className="flex items-start">{ICON}
                                        <span className="text-gray-600 dark:text-gray-300">
                                            Exclusive experience for SurpriseTable members
                                        </span>
                                    </li>
                                </ul>
                            </div>

                            {/* FORM + PRICE BREAKDOWN */}
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="notes"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Additional Notes <span className="text-xs text-gray-400">(optional)</span></FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="E.g. gluten-free, birthday, no seafood, etc."
                                                        rows={2}
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                                        <div className="mb-1 flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-300">
                                                Price per person
                                            </span>
                                            <span className="font-medium text-gray-800 dark:text-white">
                                                {formatCurrency(pricePerPerson)}
                                            </span>
                                        </div>
                                        <div className="mb-1 flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-300">
                                                Number of guests
                                            </span>
                                            <span className="font-medium text-gray-800 dark:text-white">
                                                {guestsNumber}
                                            </span>
                                        </div>
                                        <div className="flex justify-between font-bold text-base">
                                            <span className="text-gray-800 dark:text-white">Total</span>
                                            <span className="text-gray-800 dark:text-white">
                                                {formatCurrency(totalPrice)}
                                            </span>
                                        </div>
                                    </div>
                                    <motion.button
                                        type="submit"
                                        className="w-full bg-primary py-2 font-medium text-white rounded-lg hover:bg-primary/90 transition-colors duration-150 shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/60 focus:ring-offset-2 ring-offset-white dark:ring-offset-gray-800"
                                        disabled={isSubmitting}
                                        initial={{ scale: 0.98, opacity: 0 }}
                                        animate={{
                                            scale: [1, 1.07, 1],
                                            opacity: 1,
                                        }}
                                        transition={{
                                            scale: {
                                                duration: 1.4,
                                                repeat: Infinity,
                                                repeatType: "reverse",
                                                ease: "easeInOut"
                                            },
                                            opacity: { duration: 0.3 }
                                        }}
                                        whileTap={{ scale: 0.97 }}
                                    >
                                        {isSubmitting ? "Booking..." : "Confirm & Pay Now"}
                                        <motion.span
                                            className="ml-2 text-xl inline-block"
                                            initial={{ y: 0 }}
                                            animate={{ y: [0, -10, 0] }}
                                            transition={{
                                                repeat: Infinity,
                                                repeatDelay: 2.5,
                                                duration: 0.7,
                                                ease: "easeInOut",
                                                delay: 1,
                                            }}
                                        >
                                            👉
                                        </motion.span>
                                    </motion.button>
                                    <div className="w-full flex flex-col items-center mt-2">
                                        <div className="flex items-center justify-center mb-1">
                                            <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                            <span className="font-semibold text-primary text-sm">
                                                End-to-End Payment Secured
                                            </span>
                                        </div>
                                        <span className="font-semibold text-green-600 mr-2 text-xs text-center">
                                            If the final restaurant price is lower than you pay, you’ll get an automatic refund.
                                        </span>
                                        <span className="text-xs text-gray-600 dark:text-gray-400 text-center">
                                            You are only charged once.
                                            <br />
                                            By placing your order, you agree to our{" "}
                                            <a
                                                href="/terms"
                                                className="underline hover:text-primary transition"
                                            >
                                                Terms
                                            </a>{" "}
                                            and{" "}
                                            <a
                                                href="/privacy"
                                                className="underline hover:text-primary transition"
                                            >
                                                Privacy Policy
                                            </a>
                                            .
                                            <br />
                                            Your payment and personal data are fully protected.
                                        </span>
                                    </div>
                                </form>
                            </Form>
                        </div>
                    </div>
                </div>
                <AuthModal
                    isOpen={isAuthModalOpen}
                    onClose={() => setIsAuthModalOpen(false)}
                    initialView="login"
                />
            </div>
        </section>
    );
};

export default BookingShuffle;
